'use client';

/*
Barre de recherche de la homepage v5 : pilule blanche
(localisation / dates / mots-clés IA / bouton) posée sur le hero.
Réutilise Hubs, Calendar et l'autocomplétion de mots-clés de la recherche
classique (core/search.js) ainsi que la construction de la route.
*/
import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { useRouter } from 'next/compat/router';
import AsyncSelect from 'react-select/async';
import Hubs from '@/components/fields/hubs.js';
import Calendar from '@/components/fields/calendar/index.js';
import { usePatternAutocomplete } from '@/helpers/usePatternAutocomplete';
import { computeSearchRoute, navigateToSearch } from '@/core/search';
import { lang } from '@/helpers/translate';
import { useHomeCopy } from '@/components/home/homeCopy';
import { useZusTrackerStore } from '@/helpers/tracker.js';

const PATTERN_MENU_WIDTH = 320;
const PATTERN_MENU_MAX_HEIGHT = 260;

const selectClassNames = {
  control: () => '!bg-transparent !border-none !shadow-none !min-h-0 !cursor-text',
  valueContainer: () => '!p-0 !flex-wrap',
  input: () => '!text-was-ink !m-0 !p-0 !text-sm !font-medium',
  placeholder: () => '!text-was-ink/50 !text-sm !font-medium !m-0 !absolute !left-0 !whitespace-nowrap !overflow-hidden !text-ellipsis !max-w-full',
  multiValue: () => '!bg-was-violet !rounded-md !mt-0.5',
  multiValueLabel: () => '!text-white !text-xs !font-normal !px-2 !py-0.5',
  multiValueRemove: () => '!text-white hover:!bg-was-violet-dark hover:!text-white !px-1 !rounded-r-md',
  menu: () => '!bg-white !rounded-xl !border-0 !shadow-[0_1px_2px_rgba(19,14,27,0.06),0_8px_16px_-8px_rgba(19,14,27,0.25),0_28px_72px_-12px_rgba(19,14,27,0.55)] !z-50',
  menuList: () => '!p-2',
  option: (state) => (state.isFocused
    ? '!bg-was-violet/10 !text-was-violet !rounded-lg !cursor-pointer !py-2.5 !px-3 !text-left !text-sm !font-medium'
    : '!bg-transparent !text-was-ink !rounded-lg !cursor-pointer !py-2.5 !px-3 !text-left !text-sm !font-medium'),
  noOptionsMessage: () => '!text-was-muted !py-2.5 !px-3 !text-left !text-sm',
  indicatorsContainer: () => '!hidden',
};

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 'auto' }),
  valueContainer: (base) => ({ ...base, padding: 0, gap: '4px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  // `menuPlacement="auto"` + `menuPosition="fixed"` + portail : le menu est
  // positionné par rapport à la fenêtre et bascule au-dessus du champ quand il
  // serait coupé par le bas ; la marge suit le côté.
  menu: (base, state) => ({
    ...base,
    zIndex: 50,
    width: PATTERN_MENU_WIDTH,
    marginLeft: '-43px',
    ...(state.placement === 'top' ? { marginBottom: '14px' } : { marginTop: '14px' }),
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
  // Le placement (haut/bas) est décidé au montage du menu, pendant le stub de
  // chargement : on lui donne la hauteur max du menu pour que le calcul soit
  // fait avec la taille définitive (sinon un menu chargé en asynchrone déborde).
  loadingMessage: (base) => ({
    ...base,
    minHeight: PATTERN_MENU_MAX_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
};

const hubsClasses = {
  container: 'w-full',
  input: 'block w-full border-0 bg-transparent p-0 text-sm font-medium !text-was-ink placeholder:text-was-ink/50 focus:ring-0 focus:outline-none md:text-sm',
};

function PinIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-was-violet" aria-hidden="true">
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-was-violet" aria-hidden="true">
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" className="shrink-0 text-was-violet" aria-hidden="true">
      <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1z" />
      <path d="M19 2l0.9 2.6L22.5 5.5l-2.6 0.9L19 9l-0.9-2.6L15.5 5.5l2.6-0.9z" />
    </svg>
  );
}

// Même gabarit que SearchIcon : le bouton ne change pas de taille en chargement.
function SpinnerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="animate-spin" aria-hidden="true">
      <circle cx="12" cy="12" r="9" className="opacity-25" />
      <path d="M21 12a9 9 0 0 0-9-9" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/*
Même mécanique que la barre classique (core/search.js) : le champ actif
s'élargit et se colore, le séparateur voisin s'efface, le tout en 300 ms.
*/
const fieldBase = 'flex min-w-0 items-center gap-2.5 rounded-xl px-4 py-2 transition-all duration-300 ease-in-out';
const labelClass = 'text-[11px] font-normal uppercase tracking-[0.12em] text-was-muted';
const FIELD_WIDTHS = {
  hubs: { base: 'lg:flex-[1_1_250px]', active: 'lg:flex-[1.5_1_310px]' },
  calendar: { base: 'lg:flex-[0.8_1_180px]', active: 'lg:flex-[1.1_1_220px]' },
  pattern: { base: 'lg:flex-[1.2_1_300px]', active: 'lg:flex-[1.8_1_380px]' },
};

function fieldClass(key, active, hovered) {
  const w = FIELD_WIDTHS[key];
  const isActive = active === key;
  const isHovered = hovered === key;
  let tone = '';
  if (isActive) tone = 'bg-was-violet/10 shadow-[inset_0_0_0_1px_rgba(135,96,255,0.25)]';
  else if (isHovered) tone = 'bg-was-violet/7';
  return `${fieldBase} ${isActive ? w.active : w.base} ${tone}`;
}

function Divider({ hidden }) {
  return <div className={`mx-4 h-px bg-was-ink/10 transition-opacity duration-300 lg:my-2.5 lg:mx-0 lg:h-auto lg:w-px ${hidden ? 'lg:opacity-0' : 'lg:opacity-100'}`} />;
}

/*
`onSearch(routerData)` : si fourni (popin), on délègue la navigation au parent ;
sinon on navigue directement. `cityDefault` pré-remplit la localisation.
*/
export default function HeroSearch({ tx: txProp, onSearch, cityDefault }) {
  const router = useRouter();
  const home = useHomeCopy();
  const tx = txProp || home.tx;
  const langCurrent = lang();
  const { loadOptions } = usePatternAutocomplete();
  const refs = {
    refHubs: useRef(),
    refCal: useRef(),
  };
  const patternSelectRef = useRef(null);
  const fieldRefs = {
    hubs: useRef(null),
    calendar: useRef(null),
    pattern: useRef(null),
  };
  const [selectedPatterns, setSelectedPatterns] = useState([]);
  const [active, setActive] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [kwIdx, setKwIdx] = useState(0);
  const [searching, setSearching] = useState(false);

  const placeholders = tx('hero.search.keywordPlaceholders') || [];
  const placeholder = placeholders.length ? placeholders[kwIdx % placeholders.length] : '';

  useEffect(() => {
    const timer = setInterval(() => setKwIdx((i) => i + 1), 3000);
    return () => clearInterval(timer);
  }, []);

  // Clic en dehors des champs : on désactive (comme la barre classique).
  const onDocumentClick = useCallback((event) => {
    const inside = Object.values(fieldRefs).some((r) => r.current && r.current.contains(event.target));
    if (!inside) setActive(null);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [onDocumentClick]);

  const hideDividerAfter = (key, next) => active === key || active === next || hovered === next;

  // Retour arrière sur une page restaurée du bfcache après un window.location :
  // le bouton y serait resté figé en chargement.
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setSearching(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  /*
  Le loader tient du clic jusqu'à l'affichage de la search (géocodage du lieu
  + getServerSideProps) : on attend la fin de la navigation avant de le relâcher.
  */
  async function gotoSearch() {
    if (searching) return;
    setSearching(true);
    try {
      useZusTrackerStore.getState().setOrEvent('search');
      const routerData = await computeSearchRoute({
        refs, selectedPatterns, cityDefault, langCurrent,
      });
      if (!routerData) return;
      await (onSearch
        ? onSearch(routerData)
        : navigateToSearch({ router, routerData, langCurrent }));
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="relative w-full lg:w-max lg:min-w-[960px] lg:max-w-full">
      <div className="relative flex flex-col gap-1 rounded-2xl bg-white p-1.5 shadow-[0_24px_56px_rgba(19,14,27,0.2)] lg:flex-row lg:items-stretch lg:gap-0">
        <div
          ref={fieldRefs.hubs}
          className={`hubs-light ${fieldClass('hubs', active, hovered)} cursor-pointer`}
          onClick={() => {
            setActive('hubs');
            fieldRefs.hubs.current?.querySelector('input')?.focus();
          }}
          onMouseEnter={() => setHovered('hubs')}
          onMouseLeave={() => setHovered(null)}
          role="presentation"
        >
          <PinIcon />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className={labelClass}>{tx('hero.search.location')}</span>
            <Hubs
              ref={refs.refHubs}
              city={cityDefault}
              noLabel
              classesDefault={hubsClasses}
              onSelected={(hub) => {
                if (hub) {
                  setActive('calendar');
                  refs.refCal.current?.setIsOpenModal?.(true);
                }
              }}
            />
          </div>
        </div>
        <Divider hidden={hideDividerAfter('hubs', 'calendar')} />
        <div
          ref={fieldRefs.calendar}
          className={`${fieldClass('calendar', active, hovered)} cursor-pointer`}
          onClick={() => {
            setActive('calendar');
            refs.refCal.current?.setIsOpenModal?.(true);
          }}
          onMouseEnter={() => setHovered('calendar')}
          onMouseLeave={() => setHovered(null)}
          role="presentation"
        >
          <CalendarIcon />
          <div className="min-w-0 flex-1">
            <Calendar forwardRef={refs.refCal} light />
          </div>
        </div>
        <Divider hidden={hideDividerAfter('calendar', 'pattern')} />
        <div
          ref={fieldRefs.pattern}
          className={`${fieldClass('pattern', active, hovered)} cursor-text`}
          onClick={() => {
            setActive('pattern');
            patternSelectRef.current?.focus();
          }}
          onMouseEnter={() => setHovered('pattern')}
          onMouseLeave={() => setHovered(null)}
          role="presentation"
        >
          <SparklesIcon />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className={labelClass}>{tx('hero.search.keywords')}</span>
            <AsyncSelect
              ref={patternSelectRef}
              instanceId="home-hero-patterns"
              isMulti
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={typeof document === 'undefined' ? null : document.body}
              maxMenuHeight={PATTERN_MENU_MAX_HEIGHT}
              loadOptions={loadOptions}
              placeholder={placeholder}
              onChange={setSelectedPatterns}
              value={selectedPatterns}
              noOptionsMessage={() => tx('hero.search.noOptions')}
              classNames={selectClassNames}
              styles={selectStyles}
              aria-label={tx('hero.search.keywords')}
            />
          </div>
        </div>
        <button
          type="button"
          aria-label={tx('hero.search.button')}
          onClick={gotoSearch}
          disabled={searching}
          aria-busy={searching}
          className="mt-1 flex items-center justify-center gap-2 self-stretch whitespace-nowrap rounded-[10px] bg-was-ink px-6 py-3 text-[15px] font-normal text-white transition duration-200 hover:bg-was-violet active:scale-[0.98] disabled:cursor-wait disabled:bg-was-violet lg:ml-1.5 lg:mt-0 lg:self-stretch"
        >
          {searching ? <SpinnerIcon /> : <SearchIcon />}
          {tx('hero.search.button')}
        </button>
      </div>
    </div>
  );
}
